import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import { MapPin } from 'lucide-react';

// Common fix for Leaflet icons
import markerIconPng from "leaflet/dist/images/marker-icon.png";
import markerIcon2xPng from "leaflet/dist/images/marker-icon-2x.png";
import markerShadowPng from "leaflet/dist/images/marker-shadow.png";

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIconPng,
    iconRetinaUrl: markerIcon2xPng,
    shadowUrl: markerShadowPng,
});

interface AddressMapPreviewProps {
    lat?: number;
    lon?: number;
    address?: string;
}

function FlyToController({ coords }: { coords: [number, number] | null }) {
    const map = useMap();
    useEffect(() => {
        if (coords) {
            map.flyTo(coords, 16, { duration: 1.5 });
        }
    }, [coords, map]);
    return null;
}

const AddressMapPreview: React.FC<AddressMapPreviewProps> = ({ lat, lon, address }) => {
    const { t } = (useTranslation as any)('marketplace');

    if (!lat || !lon) return null;

    return (
        <div className="w-full h-40 sm:h-48 rounded-2xl overflow-hidden border-2 border-slate-100 shadow-sm relative group mt-4">
            <MapContainer
                center={[lat, lon]}
                zoom={15}
                scrollWheelZoom={false}
                zoomControl={false}
                attributionControl={false}
                className="w-full h-full z-0"
                dragging={false} // Static feel
                doubleClickZoom={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[lat, lon]}>
                    {/* Optional Popup */}
                </Marker>
                <FlyToController coords={[lat, lon]} />
            </MapContainer>

            {/* Overlay Badge */}
            <div className="absolute top-2 left-2 z-[400] bg-white/90 backdrop-blur-md px-3 py-1.5 rounded-lg shadow-sm border border-slate-200 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">{t('checkout.delivery_location', 'Lieu de livraison')}</span>
            </div>

            {/* Address Overlay */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8 z-[400] flex items-end">
                <p className="text-white text-xs font-medium truncate drop-shadow-md">
                    <MapPin className="inline w-3 h-3 mb-0.5 mr-1 text-white" />
                    {address}
                </p>
            </div>
        </div>
    );
};

export default AddressMapPreview;
